import { useState } from "react";
import { CloudDownload, Check, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { findPin, pin, unpin, useOffline, fmtBytes, supported } from "@/lib/offline";
import { useAutoPins, setAutoPlaylist } from "@/lib/autoPins";

export const PinButton = ({ type, id, name, subtitle, tracks, coverAlbumIds, lib, testid = "pin-offline-button", compact = false }) => {
  const { user } = useAuth();
  const { jobs } = useOffline();
  const { auto } = useAutoPins();
  const isAuto = type === "playlist" && auto.playlists.includes(id);
  const [busy, setBusy] = useState(false);
  const pinned = !!findPin(type, id);
  const job = jobs[`${type}:${id}`];
  if (!supported()) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      if (pinned) {
        if (isAuto) setAutoPlaylist(id, false, true); else await unpin(type, id);
        toast.success(`${name} removed from offline music`);
      } else {
        if (!tracks?.length) { toast.error("Nothing to save offline"); return; }
        await pin({ type, id, name, subtitle, tracks, coverAlbumIds, userId: user?.id, lib });
        toast.success(`${name} is available offline`, { description: `${tracks.length} song${tracks.length === 1 ? "" : "s"} saved on this device` });
      }
    } catch (e) {
      toast.error(e.code === "CAP" ? "Offline limit reached" : "Couldn't save for offline", { description: e.message });
    } finally { setBusy(false); }
  };

  const label = job ? `${job.done} / ${job.total}` : pinned ? "Available offline" : "Save offline";
  const Icon = job || busy ? Loader2 : pinned ? Check : CloudDownload;
  return (<>
    <button type="button" onClick={toggle} disabled={!!job || busy} data-testid={testid} data-pinned={pinned} title={pinned ? "Remove from offline music" : "Save for offline listening"}
      className={`flex h-12 items-center gap-2 rounded-full border px-4 text-sm transition-colors disabled:opacity-70 ${pinned ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:border-red-300 hover:bg-red-50 hover:text-red-700" : "border-zinc-300 bg-white text-zinc-700 hover:border-zinc-900"}`}>
      <Icon size={15} className={job || busy ? "animate-spin" : ""} />
      {!compact && <span data-testid={`${testid}-label`}>{label}</span>}
      {job && <span className="font-mono text-[11px] text-zinc-500">{fmtBytes(job.bytes)}</span>}
    </button>
    {type === "playlist" && pinned && !job && (
      <button type="button" onClick={() => setAutoPlaylist(id, !isAuto)} data-testid={`${testid}-auto`} data-auto={isAuto} title={isAuto ? "Stop keeping this playlist in sync offline" : "Re-download automatically whenever this playlist changes"}
        className={`flex h-12 items-center gap-1.5 rounded-full border px-3 text-xs transition-colors ${isAuto ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-zinc-300 bg-white text-zinc-600 hover:border-zinc-900"}`}>
        <RefreshCw size={12} />{isAuto ? "Auto-sync on" : "Auto-sync"}
      </button>
    )}
  </>);
};
